import React, { useEffect, useState } from 'react';
import { Moon, Sun, RotateCcw, Home, Menu, Share2, Check, Volume2, VolumeX } from 'lucide-react';
import { isMuted, toggleMuted, subscribeMuted, playOpening } from '../utils/sound.jsx';

// Share the current site: open the native share sheet when available AND copy
// the URL to the clipboard so guests can paste it anywhere. Both run in
// parallel — the share sheet is a UX bonus on mobile, the clipboard copy is
// the reliable fallback.
async function shareSite() {
  const url = typeof window !== 'undefined' ? `${window.location.origin}/` : '';
  const shareData = {
    title: "Yisehak & Lydia's Wedding",
    text: "Step into our story and join us on our special day!",
    url,
  };
  let copied = false;
  try {
    await navigator.clipboard.writeText(url);
    copied = true;
  } catch {
    // Clipboard blocked — the share sheet (if any) can still cover the user.
  }
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share(shareData);
    } catch (err) {
      // AbortError just means the user dismissed the share sheet.
      if (!err || err.name !== 'AbortError') console.warn('Share failed', err);
    }
  }
  return copied;
}

// The two-stage "confirm before doing it" pattern used by Restart: first
// click flips into a confirm state, second click commits. Any click outside
// the button resets it — kept local so it doesn't dirty the rest of the app
// on every click.
function useConfirmReset(setConfirmGameReset) {
  useEffect(() => {
    const handleGlobalClick = (e) => {
      if (!e.target.closest('.group\\/btn')) setConfirmGameReset(false);
    };
    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, [setConfirmGameReset]);
}

export function GlobalControls({
  flowStep,
  setFlowStep,
  isDarkMode,
  setIsDarkMode,
  gameMistakes,
  gameState,
  onResetGame,
  phase,
}) {
  const [confirmGameReset, setConfirmGameReset] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [muted, setMuted] = useState(isMuted);
  const [navOpen, setNavOpen] = useState(false);
  useConfirmReset(setConfirmGameReset);

  // Close the nav menu when clicking anywhere outside it
  useEffect(() => {
    if (!navOpen) return;
    const handleClick = (e) => {
      if (!e.target.closest('.compass-nav-hub')) setNavOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [navOpen]);

  useEffect(() => subscribeMuted(setMuted), []);

  const handleShareClick = async () => {
    const copied = await shareSite();
    if (copied) {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2200);
    }
  };

  return (
    <>
      {/* Top-right floating control strip */}
      <div className="fixed top-6 right-6 z-[70] flex items-center gap-2">
        {flowStep === 'game' && (
          <>
            <div
              className={`flex items-center h-10 px-3.5 rounded-full backdrop-blur-md border shadow-sm text-[11px] font-extrabold tracking-wide ${
                isDarkMode
                  ? 'bg-slate-900/80 border-white/20 text-slate-300'
                  : 'bg-white/80 border-slate-200 text-slate-600'
              }`}
            >
              Mistakes: <span className="text-rose-500 ml-1.5">{gameMistakes}</span>
            </div>

            <button
              onClick={() => setFlowStep('welcome')}
              title="Back to home"
              aria-label="Back to home"
              className={`flex items-center justify-center w-10 h-10 rounded-full backdrop-blur-md border shadow-sm transition-all hover:scale-110 active:scale-95 ${
                isDarkMode
                  ? 'bg-slate-900/80 border-white/20 text-slate-300 hover:text-purple-300'
                  : 'bg-white/80 border-slate-200 text-slate-600 hover:text-purple-600'
              }`}
            >
              <Home size={18} />
            </button>

            <button
              onClick={() => {
                if (confirmGameReset) {
                  setConfirmGameReset(false);
                  onResetGame();
                } else {
                  setConfirmGameReset(true);
                }
              }}
              title="Restart Journey"
              className={`group/btn flex items-center h-10 px-3 rounded-full backdrop-blur-md border shadow-sm transition-all duration-300 overflow-hidden hover:max-w-[160px] max-w-[40px] ${
                confirmGameReset
                  ? 'bg-amber-600 text-white border-amber-500 font-extrabold max-w-[160px]'
                  : isDarkMode
                    ? 'bg-slate-900/80 border-white/20 text-slate-300 hover:text-amber-400'
                    : 'bg-white/80 border-slate-200 text-slate-600 hover:text-amber-600'
              }`}
            >
              <RotateCcw
                size={16}
                className={`shrink-0 ${confirmGameReset ? 'text-white animate-spin' : 'text-amber-500'}`}
              />
              <span
                className={`text-xs font-extrabold pl-2 transition-opacity whitespace-nowrap ${
                  confirmGameReset ? 'opacity-100' : 'opacity-0 group-hover/btn:opacity-100'
                }`}
              >
                {confirmGameReset ? 'Confirm?' : 'Restart'}
              </span>
            </button>
          </>
        )}

        <button
          onClick={handleShareClick}
          title={shareCopied ? 'Link copied!' : 'Share this site'}
          aria-label="Share this site"
          className={`flex items-center justify-center w-10 h-10 rounded-full backdrop-blur-md border shadow-sm transition-all hover:scale-110 active:scale-95 ${
            shareCopied
              ? 'bg-emerald-500 border-emerald-400 text-white'
              : isDarkMode
                ? 'bg-slate-900/80 border-white/20 text-purple-300'
                : 'bg-white/80 border-slate-200 text-purple-600'
          }`}
        >
          {shareCopied ? <Check size={18} /> : <Share2 size={18} />}
        </button>

        {flowStep === 'game' && (
          <button
            onClick={() => toggleMuted()}
            title={muted ? 'Unmute sound effects' : 'Mute sound effects'}
            aria-label={muted ? 'Unmute sound effects' : 'Mute sound effects'}
            className={`flex items-center justify-center w-10 h-10 rounded-full backdrop-blur-md border shadow-sm transition-all hover:scale-110 active:scale-95 ${
              isDarkMode
                ? 'bg-slate-900/80 border-white/20 text-purple-300'
                : 'bg-white/80 border-slate-200 text-purple-600'
            }`}
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        )}

        <button
          onClick={() => setIsDarkMode(!isDarkMode)}
          title={isDarkMode ? 'Switch to Day Mode' : 'Switch to Night Mode'}
          className={`flex items-center justify-center w-10 h-10 rounded-full backdrop-blur-md border shadow-sm transition-all hover:scale-110 active:scale-95 ${
            isDarkMode
              ? 'bg-slate-900/80 border-white/20 text-amber-400'
              : 'bg-white/80 border-slate-200 text-slate-700'
          }`}
        >
          {isDarkMode ? <Sun size={18} className="text-amber-400" /> : <Moon size={18} className="text-sky-500" />}
        </button>
      </div>

      {/* Bottom-right hover navigation hub — hidden on the game screen */}
      {flowStep !== 'game' && (
      <div className="compass-nav-hub fixed bottom-6 right-6 z-[70] group/nav">
        <div
          className={`absolute bottom-full right-0 pt-3 flex flex-col gap-1.5 transition-all duration-200 origin-bottom-right ${
            navOpen
              ? 'pointer-events-auto opacity-100 visible translate-y-0'
              : 'pointer-events-none opacity-0 invisible translate-y-2 group-hover/nav:pointer-events-auto group-hover/nav:opacity-100 group-hover/nav:visible group-hover/nav:translate-y-0'
          }`}
        >
          <div
            className={`flex flex-col gap-1 p-3 rounded-2xl backdrop-blur-xl border shadow-2xl min-w-[160px] ${
              isDarkMode ? 'bg-slate-900/95 border-white/10 text-white' : 'bg-white/95 border-slate-200 text-slate-900'
            }`}
          >
            <span className="text-[9px] font-extrabold text-slate-400 px-2 uppercase tracking-widest mb-1">
              Explore
            </span>
            <button
              onClick={() => { setFlowStep('welcome'); setNavOpen(false); }}
              className="text-left px-3 py-2 rounded-xl text-xs font-bold hover:bg-purple-500/10 hover:text-purple-500 transition-colors"
            >
              Welcome
            </button>
            <button
              onClick={() => {
                if (flowStep !== 'game' && gameState.p1Step === 0 && gameState.p2Step === 0 && gameState.status === 'playing') playOpening();
                setFlowStep('game');
                setNavOpen(false);
              }}
              className="text-left px-3 py-2 rounded-xl text-xs font-bold hover:bg-purple-500/10 hover:text-purple-500 transition-colors"
            >
              Play Story Game
            </button>
            {/* The RSVP is only a destination while the invitation is up. From
                the final fortnight onward replies are in, so the menu stops
                offering a form nobody should be filling in — the schedule
                takes its place as the thing guests come here for. */}
            {phase === 'invitation' && (
              <button
                onClick={() => { setFlowStep('rsvp'); setNavOpen(false); }}
                className="text-left px-3 py-2 rounded-xl text-xs font-bold hover:bg-purple-500/10 hover:text-purple-500 transition-colors"
              >
                RSVP Form
              </button>
            )}
            {/* The day-of pages join the menu once the hub is up, and stay
                after the wedding — the gallery and the stream recordings are
                what guests come back for. */}
            {phase !== 'invitation' &&
              [
                ['gallery', 'Gallery'],
                ['livestream', 'Livestream'],
                ['photobooth', 'Photo Booth'],
              ].map(([route, label]) => (
                <button
                  key={route}
                  onClick={() => { setFlowStep(route); setNavOpen(false); }}
                  className="text-left px-3 py-2 rounded-xl text-xs font-bold hover:bg-purple-500/10 hover:text-purple-500 transition-colors"
                >
                  {label}
                </button>
              ))}
            <button
              onClick={() => { setFlowStep('guestbook'); setNavOpen(false); }}
              className="text-left px-3 py-2 rounded-xl text-xs font-bold hover:bg-purple-500/10 hover:text-purple-500 transition-colors"
            >
              Guestbook
            </button>
            <button
              onClick={() => { setFlowStep('timeline'); setNavOpen(false); }}
              className="text-left px-3 py-2 rounded-xl text-xs font-bold hover:bg-purple-500/10 hover:text-purple-500 transition-colors"
            >
              Details &amp; Schedule
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setNavOpen((v) => !v)}
          aria-expanded={navOpen}
          aria-label="Open navigation menu"
          className="w-14 h-14 rounded-full bg-gradient-to-tr from-primary to-secondary text-white shadow-[0_8px_30px_rgba(194,50,106,0.5)] flex items-center justify-center border border-white/20 transition-transform duration-300 hover:scale-105 active:scale-95"
        >
          <Menu size={24} />
        </button>
      </div>
      )}
    </>
  );
}
